import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Globe2,
  Plane,
  Scale,
  Banknote,
  ShieldCheck,
  FileText,
  Phone,
  ArrowRight,
  Check,
  Building2,
  Compass,
} from "lucide-react";
import api from "@/lib/api";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const SERVICES = [
  {
    icon: Compass,
    title: "Investment Advisory",
    body: "Curated Indian and overseas inventory hand-picked for NRIs — RERA-verified, freehold, golden-visa-eligible.",
  },
  {
    icon: Scale,
    title: "FEMA & RBI Compliance",
    body: "End-to-end guidance on FEMA, RBI guidelines, repatriation of sale proceeds and TDS optimisation.",
  },
  {
    icon: Banknote,
    title: "Home Loans for NRIs",
    body: "Tied-up with leading banks for NRI / OCI / PIO home loans up to 80% funding with attractive rates.",
  },
  {
    icon: FileText,
    title: "Documentation & PoA",
    body: "Drafting and notarisation of Power of Attorney, Sale Deed, Gift Deed — coordinated across embassies and India.",
  },
  {
    icon: Building2,
    title: "Property Management",
    body: "Tenant sourcing, rent collection, society dues, repairs, annual audits — your asset, on autopilot.",
  },
  {
    icon: ShieldCheck,
    title: "Title & Legal Due Diligence",
    body: "Title search, encumbrance, RERA & RWA approvals reviewed by our in-house legal panel before you commit.",
  },
];

const INBOUND = [
  "Lucknow · Sushant Golf City · Gomti Nagar",
  "Gurugram · Golf Course Road · Sohna Road",
  "Noida · Sector 150 · Yamuna Expressway",
  "Mumbai · Bandra · Worli · Thane",
  "Bangalore · Whitefield · ORR",
  "Pune · Hinjewadi · Kharadi",
  "Delhi · Lutyens · Moti Nagar",
  "Hyderabad · Banjara Hills · Gachibowli",
];

const OUTBOUND = [
  "Dubai · Downtown · Palm Jumeirah · Marina",
  "Singapore · Marina Bay · Orchard · Sentosa",
];

const FAQS = [
  {
    q: "Can NRIs buy residential property in India?",
    a: "Yes. NRIs, OCIs and PIOs can buy any number of residential or commercial properties in India without RBI approval. Agricultural land, plantations and farmhouses require special permission.",
  },
  {
    q: "How do I repatriate the sale proceeds?",
    a: "Sale proceeds of up to two residential properties can be repatriated, subject to documentation under FEMA. Our team coordinates the entire 15CA / 15CB process with your CA and bank.",
  },
  {
    q: "What home-loan rates are available for NRIs?",
    a: "Indian banks currently offer NRI home loans starting from 8.5% p.a. with up to 80% LTV and tenures of 20–30 years. We negotiate the best terms across HDFC, ICICI, SBI and Axis.",
  },
  {
    q: "Do you handle property management for absent owners?",
    a: "Yes. Our concierge team manages tenant sourcing, rent collection, society payments and yearly maintenance, with quarterly photo audits delivered straight to your inbox.",
  },
  {
    q: "Can I buy in Dubai or Singapore through OC Infraventures?",
    a: "Absolutely. We are tied up with master developers (Emaar, Damac, CapitaLand, Far East Organization) and offer exclusive launch-day allocations, golden-visa documentation, and end-to-end conveyancing.",
  },
];

export default function NriServices() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "I'm an NRI exploring real estate options. Please reach out.",
  });
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiry", { ...form, consent: true });
      toast.success("Thank you. Our NRI desk will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="nri-services-page" className="pb-32">
      {/* Hero */}
      <section className="relative h-[78vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=2400"
            alt="Global aviation horizon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-16">
          <div className="overline mb-5 flex items-center gap-2">
            <Plane className="w-3.5 h-3.5" /> NRI · OCI · PIO Desk
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 font-light leading-[1.05] max-w-4xl">
            One realty partner. <em className="text-[#D4A437] not-italic">Both sides</em><br />
            of the diaspora.
          </h1>
          <p className="text-slate-700 text-lg mt-8 max-w-2xl font-light leading-relaxed">
            Whether you&apos;re investing back home in India or buying that golden-visa apartment in Dubai —
            our dedicated NRI desk handles every step, in your timezone, in your language.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#nri-contact"
              data-testid="nri-cta-talk"
              className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-7 py-3.5 transition-colors"
            >
              Talk to NRI Desk <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/projects"
              data-testid="nri-cta-projects"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#D4A437] hover:text-[#D4A437] text-slate-800 px-7 py-3.5 transition-colors"
            >
              Explore Inventory
            </Link>
          </div>
        </div>
      </section>

      {/* Two-way value prop */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32 grid md:grid-cols-2 gap-px bg-slate-100">
        <div className="bg-white p-10 md:p-14">
          <div className="overline mb-5 flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> Inbound</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">
            NRIs <em className="text-[#D4A437] not-italic">investing</em> back home in India.
          </h2>
          <p className="text-slate-600 mt-6 leading-relaxed font-light">
            From a 2BHK in Gomti Nagar to a Bandra sea-view penthouse — we curate, document, and manage
            every Indian asset class for non-resident investors. FEMA-compliant, RBI-aligned, transparent.
          </p>
          <ul className="mt-8 space-y-3">
            {INBOUND.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm text-slate-700">
                <Check className="w-4 h-4 text-[#D4A437] mt-0.5 shrink-0" strokeWidth={2} /> {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white p-10 md:p-14">
          <div className="overline mb-5 flex items-center gap-2"><Plane className="w-3.5 h-3.5" /> Outbound</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">
            Indians buying <em className="text-[#D4A437] not-italic">overseas</em>.
          </h2>
          <p className="text-slate-600 mt-6 leading-relaxed font-light">
            Tax-free yields, currency-pegged appreciation, and golden-visa eligibility — Dubai
            and Singapore inventory through direct allocations from master developers.
          </p>
          <ul className="mt-8 space-y-3">
            {OUTBOUND.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm text-slate-700">
                <Check className="w-4 h-4 text-[#D4A437] mt-0.5 shrink-0" strokeWidth={2} /> {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">What we cover</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-16 max-w-3xl">
          End-to-end services for the global Indian.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100">
          {SERVICES.map((s) => (
            <div key={s.title} className="bg-white p-8" data-testid={`nri-service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <s.icon className="w-6 h-6 text-[#D4A437] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-lg text-slate-900 mb-3">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">How it works</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-16 max-w-3xl">
          From discovery call to keys in hand — typically 45 days.
        </h2>
        <div className="grid md:grid-cols-4 gap-px bg-slate-100">
          {[
            { n: "01", t: "Discovery Call", d: "30-min call (any timezone) to understand budget, horizon, and end-use." },
            { n: "02", t: "Curated Shortlist", d: "Three to five RERA / freehold options — virtual walkthroughs included." },
            { n: "03", t: "Documentation", d: "PoA, KYC, FEMA forms, escrow, home-loan sanction — all online." },
            { n: "04", t: "Handover & Beyond", d: "Possession, registration, tenant onboarding and quarterly reports." },
          ].map((s) => (
            <div key={s.n} className="bg-white p-8">
              <div className="overline mb-4">{s.n}</div>
              <h3 className="font-serif text-lg text-slate-900 mb-3">{s.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-serif text-4xl text-slate-900 font-light leading-tight">
            Answers to the questions our NRI clients ask most.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-6">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="border border-slate-200 px-6 py-5 group open:border-amber-200 transition-colors"
              data-testid={`nri-faq-${i}`}
            >
              <summary className="font-serif text-lg text-slate-900 cursor-pointer list-none flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-[#D4A437] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section
        id="nri-contact"
        className="max-w-7xl mx-auto px-6 md:px-12 mt-32 bg-amber-50/40 border border-slate-200 p-10 md:p-16 grid md:grid-cols-12 gap-12"
      >
        <div className="md:col-span-5">
          <div className="overline mb-4">NRI Desk</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">
            Schedule a private consultation.
          </h2>
          <p className="text-slate-600 mt-6 font-light leading-relaxed">
            One of our senior relationship managers will reach out within 24 hours, in your preferred
            timezone. No obligation — just a clear roadmap.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4A437]" /> +91 522 4000 800 (India)</li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4A437]" /> +971 4 000 0000 (UAE Desk)</li>
            <li className="flex items-center gap-3"><Globe2 className="w-4 h-4 text-[#D4A437]" /> nri@ocinfraventures.com</li>
          </ul>
        </div>
        <form
          onSubmit={handleSubmit}
          className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
          data-testid="nri-contact-form"
        >
          <div className="sm:col-span-1">
            <label className="overline block mb-2">Full Name</label>
            <input
              data-testid="nri-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500"
              required
            />
          </div>
          <div className="sm:col-span-1">
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              data-testid="nri-input-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Phone (with country code)</label>
            <input
              data-testid="nri-input-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+971 50 ..."
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">What are you looking for?</label>
            <textarea
              data-testid="nri-input-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div className="sm:col-span-2">
            <ConsentCheckbox checked={consent} onChange={setConsent} testId="inq-consent-nri" />
            <button
              type="submit"
              disabled={submitting || !consent}
              data-testid="nri-submit-btn"
              className="w-full sm:w-auto mt-5 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-8 py-3.5 transition-colors disabled:opacity-50"
            >
              {submitting ? "Sending..." : "Request Consultation →"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
